import React, { useState } from 'react';
import { formatPrice } from '../../utils/formatHelper';
import {
  ORDER_STATUS, ORDER_STATUS_TEXT, ORDER_STATUS_STYLES,
  SHIPMENT_STATUS_TEXT, SHIPMENT_STATUS_STYLES,
  ORDER_TIMELINE_FLOW, ORDER_TERMINAL_STATUSES,
  ORDER_TIMELINE_TIMESTAMP_MAP, SHIPMENT_TIMELINE_FLOW
} from '../../constants/orderConstants';
import placeholderImage from '../../assets/images/placeholder.jpg';

const OrderDetails = ({ order, onAssignCarrier }) => {
  const [copiedField, setCopiedField] = useState(null);

  if (!order) return null;

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, field }) => (
    <button
      className="btn btn-light btn-sm rounded-circle shadow-sm ms-2 d-inline-flex align-items-center justify-content-center"
      style={{ width: '24px', height: '24px', padding: 0 }}
      onClick={() => handleCopy(text, field)}
      title="Kopyala"
    >
      <i className={`fas ${copiedField === field ? 'fa-check text-success' : 'fa-copy text-muted'}`} style={{ fontSize: '10px' }}></i>
    </button>
  );

  const SectionHeader = ({ title, icon }) => (
    <div className="mb-3 border-bottom pb-2">
      <h6 className="mb-0 fw-bold text-dark">
        {icon && <i className={`fas ${icon} me-2 text-muted`}></i>}
        {title}
      </h6>
    </div>
  );

  // ─── Helpers ────────────────────────────────────────────────────

  const formatTimestamp = (raw) => {
    if (!raw) return null;
    try {
      return new Date(raw).toLocaleDateString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return null; }
  };

  const isTerminal = ORDER_TERMINAL_STATUSES.includes(order.status);

  // ─── Timeline Steps Builder ────────────────────────────────────

  const buildTimelineSteps = () => {
    const currentStatus = order.status;
    const flowIdx = ORDER_TIMELINE_FLOW.indexOf(currentStatus);

    // Determine which happy-path steps to show
    let steps;
    if (isTerminal) {
      // Terminal status: show completed happy-path steps up to the point of termination,
      // then append the terminal status as the final step.
      // Find the last completed happy-path step before termination.
      // Heuristic: if shipment exists → at least PROCESSING was reached; if shipment was delivered → DELIVERED.
      let lastReachedIdx = 0; // at minimum PENDING was reached
      if (order.shipment) {
        lastReachedIdx = Math.max(lastReachedIdx, 1); // PROCESSING
        const shippedStatuses = ['PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'FAILED', 'RETURNED'];
        if (order.shipment.status && shippedStatuses.includes(order.shipment.status)) {
          lastReachedIdx = Math.max(lastReachedIdx, 2); // SHIPPED
        }
        if (order.shipment.status === 'DELIVERED') {
          lastReachedIdx = Math.max(lastReachedIdx, 3); // DELIVERED
        }
      }

      steps = ORDER_TIMELINE_FLOW.slice(0, lastReachedIdx + 1).map(status => ({
        key: status,
        label: ORDER_STATUS_TEXT[status],
        completed: true,
        active: false,
        isTerminal: false,
        timestamp: formatTimestamp(ORDER_TIMELINE_TIMESTAMP_MAP[status]?.(order))
      }));

      // Append terminal step
      const terminalStyle = ORDER_STATUS_STYLES[currentStatus] || {};
      steps.push({
        key: currentStatus,
        label: ORDER_STATUS_TEXT[currentStatus] || currentStatus,
        completed: false,
        active: true,
        isTerminal: true,
        terminalColor: terminalStyle.text,
        timestamp: formatTimestamp(ORDER_TIMELINE_TIMESTAMP_MAP[currentStatus]?.(order))
      });
    } else if (flowIdx !== -1) {
      // Normal happy-path
      steps = ORDER_TIMELINE_FLOW.map((status, idx) => ({
        key: status,
        label: ORDER_STATUS_TEXT[status],
        completed: idx < flowIdx || currentStatus === ORDER_STATUS.DELIVERED,
        active: idx === flowIdx,
        isTerminal: false,
        timestamp: formatTimestamp(ORDER_TIMELINE_TIMESTAMP_MAP[status]?.(order))
      }));
    } else {
      // Unknown status — show just that status
      steps = [{
        key: currentStatus,
        label: ORDER_STATUS_TEXT[currentStatus] || currentStatus,
        completed: false,
        active: true,
        isTerminal: false,
        timestamp: formatTimestamp(order.createdAt)
      }];
    }

    return steps;
  };

  const timelineSteps = buildTimelineSteps();

  // ─── Shipment Mini-Timeline Builder ────────────────────────────

  const buildShipmentMiniSteps = () => {
    const shipment = order.shipment;
    if (!shipment) return null;

    const shipmentFlowIdx = SHIPMENT_TIMELINE_FLOW.indexOf(shipment.status);
    // If shipment status is not in the flow (e.g. FAILED, CANCELLED, RETURNED), find the last reached step
    const history = Array.isArray(shipment.trackingHistory) ? shipment.trackingHistory : [];

    return SHIPMENT_TIMELINE_FLOW.map((status, idx) => {
      const historyEntry = history.find(h => h.status === status);
      let completed = false;
      let active = false;

      if (shipmentFlowIdx !== -1) {
        // Status is in the normal flow
        completed = idx < shipmentFlowIdx || shipment.status === 'DELIVERED';
        active = idx === shipmentFlowIdx;
      } else {
        // Status is anomalous (FAILED/CANCELLED/RETURNED) — mark based on history
        completed = !!historyEntry;
      }

      return {
        key: status,
        label: SHIPMENT_STATUS_TEXT[status],
        completed,
        active,
        timestamp: historyEntry?.timestamp ? formatTimestamp(historyEntry.timestamp) : null
      };
    });
  };

  // Whether shipment detail should show
  const showShipmentDetail = order.shipment &&
    (order.status === ORDER_STATUS.SHIPPED ||
     order.status === ORDER_STATUS.DELIVERED ||
     (isTerminal && order.shipment.status));

  const shipmentMiniSteps = showShipmentDetail ? buildShipmentMiniSteps() : null;

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="order-details-refined">
      {/* Top Banner Stats */}
      <div className="row g-2 mb-4">
        <div className="col-12">
          <div className="bg-light p-3 rounded d-flex justify-content-between align-items-center border">
            <div>
              <span className="text-muted small d-block mb-1">Toplam Tutar</span>
              <span className="fw-bold fs-5 text-dark">{formatPrice(order.totalAmount)}</span>
            </div>
            <div className="text-end">
              <span className="text-muted small d-block mb-1">Sipariş Tarihi</span>
              <span className="fw-bold text-dark d-flex align-items-center justify-content-end gap-2">
                <i className="far fa-calendar-alt text-muted"></i>
                {new Date(order.createdAt).toLocaleDateString('tr-TR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Left Column: Info */}
        <div className="col-md-7 border-end">
          {/* Customer Info */}
          <div className="mb-4">
            <SectionHeader title="Müşteri" icon="fa-user" />
            <div className="ps-0">
              <div className="d-flex align-items-center mb-2">
                <div>
                  <div className="fw-bold text-dark small fs-6">{order.user.fullName}</div>
                  <div className="d-flex align-items-center mt-1">
                    <span className="text-muted small">{order.user.email}</span>
                    <CopyButton text={order.user.email} field="email" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="mb-4">
            <SectionHeader title="Teslimat Adresi" icon="fa-map-marker-alt" />
            <div className="ps-0">
              <div className="text-dark small lh-base">
                <div className="fw-medium mb-1">{order.shippingAddress.contactName}</div>
                {order.shippingAddress.address}
                <br />
                {order.shippingAddress.district && `${order.shippingAddress.district}, `}
                {order.shippingAddress.city} / {order.shippingAddress.country}
                <div className="mt-1 d-flex align-items-center">
                  <span className="fw-medium text-dark">{order.shippingAddress.zipCode}</span>
                  <CopyButton
                    text={`${order.shippingAddress.address} ${order.shippingAddress.city}/${order.shippingAddress.country}`}
                    field="address"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Timeline */}
        <div className="col-md-5 ps-4">
          <SectionHeader title="Sipariş Süreci" icon="fa-history" />

          {['PENDING', 'PROCESSING', 'SHIPPED'].includes(order.status) && onAssignCarrier && (
            <div className="mb-3 text-start">
              <button
                className="btn btn-dark btn-sm"
                style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
                onClick={onAssignCarrier}
              >
                Kargo
              </button>
            </div>
          )}

          {/* Terminal Status Banner */}
          {isTerminal && (
            <div
              className="d-flex align-items-start gap-2 p-2 rounded mb-3"
              style={{
                backgroundColor: ORDER_STATUS_STYLES[order.status]?.bg || '#f8f9fa',
                border: `1px solid ${ORDER_STATUS_STYLES[order.status]?.text || '#dee2e6'}20`
              }}
            >
              <i
                className={`fas fa-${ORDER_STATUS_STYLES[order.status]?.icon || 'info-circle'} mt-1`}
                style={{ color: ORDER_STATUS_STYLES[order.status]?.text, fontSize: '0.85rem' }}
              ></i>
              <div style={{ fontSize: '0.8rem' }}>
                <div className="fw-bold" style={{ color: ORDER_STATUS_STYLES[order.status]?.text }}>
                  {ORDER_STATUS_TEXT[order.status]}
                </div>
                {order.metadata?.cancelledBy && (
                  <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                    {order.metadata.cancelledBy.role === 'SUPER_ADMIN' ? 'Admin' : 'Kullanıcı'} tarafından
                  </div>
                )}
                {formatTimestamp(ORDER_TIMELINE_TIMESTAMP_MAP[order.status]?.(order)) && (
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    {formatTimestamp(ORDER_TIMELINE_TIMESTAMP_MAP[order.status]?.(order))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="mt-2">
            {timelineSteps.map((step, idx) => {
              const isLast = idx === timelineSteps.length - 1;
              const isShippedStep = step.key === ORDER_STATUS.SHIPPED;
              // Determine dot colors
              let dotBg, dotBorder, dotContent, labelColor;
              if (step.isTerminal) {
                dotBg = step.terminalColor;
                dotBorder = step.terminalColor;
                dotContent = <i className={`fas fa-${ORDER_STATUS_STYLES[step.key]?.icon || 'circle'}`} style={{ fontSize: '0.55rem', color: '#fff' }}></i>;
                labelColor = step.terminalColor;
              } else if (step.completed) {
                dotBg = '#198754';
                dotBorder = '#198754';
                dotContent = <i className="fas fa-check" style={{ fontSize: '0.55rem', color: '#fff' }}></i>;
                labelColor = '#495057';
              } else if (step.active) {
                dotBg = '#fff';
                dotBorder = ORDER_STATUS_STYLES[step.key]?.text || '#0d6efd';
                dotContent = <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: dotBorder }}></div>;
                labelColor = dotBorder;
              } else {
                dotBg = '#fff';
                dotBorder = '#dee2e6';
                dotContent = null;
                labelColor = '#adb5bd';
              }

              // Connector line color
              const nextStep = timelineSteps[idx + 1];
              const connectorColor = step.completed && nextStep && (nextStep.completed || nextStep.active)
                ? '#198754'
                : '#e9ecef';

              return (
                <div key={step.key} className="d-flex position-relative" style={{ paddingBottom: isLast ? 0 : '1.25rem' }}>
                  {/* Connector line */}
                  {!isLast && (
                    <div
                      className="position-absolute"
                      style={{ left: 9, top: 22, bottom: 0, width: 2, backgroundColor: connectorColor }}
                    />
                  )}

                  {/* Dot */}
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      backgroundColor: dotBg,
                      border: `2px solid ${dotBorder}`,
                      zIndex: 1
                    }}
                  >
                    {dotContent}
                  </div>

                  {/* Content */}
                  <div className="ms-3" style={{ paddingTop: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-baseline gap-2">
                      <span className="fw-semibold" style={{ fontSize: '0.8rem', color: labelColor, whiteSpace: 'nowrap' }}>
                        {step.label}
                      </span>
                      {step.timestamp && (
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>{step.timestamp}</span>
                      )}
                    </div>

                    {/* Shipment info under SHIPPED step */}
                    {isShippedStep && (step.active || step.completed) && order.shipment && (
                      <div className="mt-1" style={{ fontSize: '0.75rem' }}>
                        {/* Carrier + current status */}
                        <div className="d-flex align-items-center gap-1 text-muted">
                          {order.shipment.carrier?.name && (
                            <span className="fw-medium text-dark">{order.shipment.carrier.name}</span>
                          )}
                          {order.shipment.carrier?.name && <span>—</span>}
                          <span
                            className="badge rounded-pill border-0 py-1 px-2"
                            style={{
                              backgroundColor: SHIPMENT_STATUS_STYLES[order.shipment.status]?.bg || '#f8f9fa',
                              color: SHIPMENT_STATUS_STYLES[order.shipment.status]?.text || '#6c757d',
                              fontSize: '0.7rem'
                            }}
                          >
                            {SHIPMENT_STATUS_TEXT[order.shipment.status] || order.shipment.status}
                          </span>
                        </div>

                        {/* Tracking number */}
                        {order.shipment.trackingNumber && (
                          <div className="d-flex align-items-center gap-1 mt-1">
                            <span className="text-muted" style={{ fontSize: '0.72rem' }}>Takip:</span>
                            <span className="text-dark fw-medium" style={{ fontSize: '0.72rem' }}>
                              {order.shipment.trackingNumber}
                            </span>
                            <CopyButton text={order.shipment.trackingNumber} field="tracking" />
                          </div>
                        )}

                        {/* Shipment mini-timeline */}
                        {shipmentMiniSteps && shipmentMiniSteps.length > 0 && (
                          <div className="mt-2 ps-1" style={{ borderLeft: '1px solid #e9ecef' }}>
                            {shipmentMiniSteps.map((ms) => {
                              let miniDotColor;
                              if (ms.completed) miniDotColor = '#198754';
                              else if (ms.active) miniDotColor = SHIPMENT_STATUS_STYLES[ms.key]?.text || '#0d6efd';
                              else miniDotColor = '#dee2e6';

                              return (
                                <div key={ms.key} className="d-flex align-items-center gap-2 py-1 ps-2">
                                  <div
                                    className="rounded-circle flex-shrink-0"
                                    style={{
                                      width: 6, height: 6,
                                      backgroundColor: miniDotColor
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      color: ms.completed || ms.active ? '#495057' : '#adb5bd'
                                    }}
                                  >
                                    {ms.label}
                                  </span>
                                  {ms.timestamp && (
                                    <span className="text-muted" style={{ fontSize: '0.65rem' }}>{ms.timestamp}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="mt-3 pt-3 border-top">
        <SectionHeader title="Ürünler" icon="fa-box" />
        <div className="table-responsive">
          <table className="table table-borderless align-middle mb-0" style={{ fontSize: '0.9rem' }}>
            <thead className="border-bottom">
              <tr>
                <th className="ps-0 py-2 text-muted fw-normal">Ürün Detayı</th>
                <th className="py-2 text-center text-muted fw-normal" style={{ width: '80px' }}>Adet</th>
                <th className="pe-0 py-2 text-end text-muted fw-normal" style={{ width: '100px' }}>Toplam</th>
              </tr>
            </thead>
            <tbody>
              {order.products.map((product) => (
                <tr key={product.id} className="border-bottom border-light">
                  <td className="ps-0 py-3">
                    <div className="d-flex align-items-center">
                      <img
                        src={product.mainImage || placeholderImage}
                        alt={product.name}
                        className="rounded border"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      />
                      <div className="ms-3">
                        <div className="fw-medium text-dark text-truncate" style={{ maxWidth: '200px' }}>{product.name}</div>
                        <div className="text-muted small">{formatPrice(product.price)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center py-3 text-dark">{product.quantity}</td>
                  <td className="pe-0 py-3 text-end fw-bold text-dark">{formatPrice(product.price * product.quantity)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2" className="text-end pe-3 py-2 text-muted small">Ara Toplam</td>
                <td className="text-end pe-0 py-2 fw-medium">{formatPrice(order.totalAmount + order.discount_amount)}</td>
              </tr>
              {order.discount_amount > 0 && (
                <tr>
                  <td colSpan="2" className="text-end pe-3 py-0 text-danger small">İndirim</td>
                  <td className="text-end pe-0 py-0 text-danger small">-{formatPrice(order.discount_amount)}</td>
                </tr>
              )}
              <tr>
                <td colSpan="2" className="text-end pe-3 py-3 fw-bold text-dark fs-6">Genel Toplam</td>
                <td className="text-end pe-0 py-3 fw-bold text-primary fs-6">{formatPrice(order.totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
